import { useEffect, useState } from 'react';

import { Backdrop, Box, SxProps, Theme, Typography } from '@mui/material';

import { useLanguage } from '../../hooks';
import { RocketIllustration } from '../../illustrations';

interface Props {
  /** Name of the account being entered, or null when no switch is running. */
  accountName: string | null;
}

/**
 * How long the row keeps the floor before the overlay takes it.
 *
 * The two pieces of feedback answer the same question and the overlay sits above
 * the popover, so appearing at once it simply erased the row spinner. Handing it
 * the first moments makes the click feel answered where it was made, and the
 * overlay then covers the seconds that actually need covering. Short enough that
 * a slow POST is never left with nothing on screen.
 */
const OVERLAY_DELAY_MS = 400;

const customStyles = {
  backdrop: {
    backgroundColor: 'rgba(43, 55, 80, 0.45)',
    zIndex: (theme) => theme.zIndex.modal + 1,
  },
  card: {
    width: '420px',
    maxWidth: '90%',
    padding: '40px 32px',
    borderRadius: '16px',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  // Sizes and timings are design's own CSS from Figma. The pale circle lives here
  // and not inside the illustration because that CSS moves the rocket only: baked
  // into the asset, the background would travel with it.
  loader: {
    width: '120px',
    height: '120px',
    marginBottom: '8px',
    position: 'relative',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    backgroundColor: '#E8F1FE',
  },
  rocket: {
    width: '64px',
    height: '64px',
    filter: 'drop-shadow(0 2px 6px rgba(0, 0, 0, 0.25))',
    animation: 'accountSwitchPrelaunch 1.1s ease-in-out infinite',
    '@keyframes accountSwitchPrelaunch': {
      '0%, 100%': { transform: 'translateY(0)' },
      '50%': { transform: 'translateY(-6px)' },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  flame: {
    position: 'absolute',
    bottom: '28px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background:
      'radial-gradient(ellipse at center, rgba(255, 190, 70, 0.95) 0%, rgba(255, 110, 0, 0.75) 60%, rgba(255, 110, 0, 0) 70%)',
    filter: 'blur(2px)',
    animation: 'accountSwitchThrust 1.1s ease-in-out infinite',
    '@keyframes accountSwitchThrust': {
      '0%, 100%': { transform: 'scale(0.7)', opacity: 0.6 },
      '50%': { transform: 'scale(1.15)', opacity: 1 },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      opacity: 0.6,
    },
  },
  title: {
    fontSize: '18px',
    fontWeight: 500,
    color: '#3B3C40',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '14px',
    lineHeight: '22px',
    color: '#6E7178',
    textAlign: 'center',
  },
} satisfies Record<string, SxProps<Theme>>;

/**
 * What the page shows while a switch is in flight.
 *
 * It covers the page on purpose: from the moment the switch starts, everything
 * behind it belongs to the account being left, and the session is replaced a
 * moment later by a document navigation. Without this the person watches the old
 * account's data sit there for the seconds the POST and the load take.
 */
export function AccountSwitchTransition({ accountName }: Props) {
  const lang = useLanguage();
  const copy = lang.mainTopBar.accountSwitcher;

  const [covering, setCovering] = useState(false);

  useEffect(() => {
    if (accountName === null) {
      setCovering(false);
      return;
    }
    const timer = setTimeout(() => setCovering(true), OVERLAY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [accountName]);

  // Unmounted rather than kept open={false}: a Backdrop that stays in the tree
  // still answers queries and still sits over the page for anything that reads
  // the DOM, and this overlay is the only thing between the person and the app.
  if (accountName === null || !covering) return null;

  return (
    <Backdrop
      open
      sx={customStyles.backdrop}
      data-testid='account-switch-transition'
    >
      <Box sx={customStyles.card}>
        <Box sx={customStyles.loader}>
          <RocketIllustration sx={customStyles.rocket} />
          <Box sx={customStyles.flame} />
        </Box>
        <Typography sx={customStyles.title}>
          {copy.switchingTitle.replace('{{account}}', accountName)}
        </Typography>
        <Typography sx={customStyles.subtitle}>{copy.switchingSubtitle}</Typography>
      </Box>
    </Backdrop>
  );
}
